/**
 * Customer Ops Portal — User Management
 * Real CRUD against /api/v1/users (tenant-scoped).
 */
import { useState, useEffect } from 'react';
import { opsApi } from '@/services/api';
import { useTrackOticAuth } from '@/context/TrackOticAuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Users, UserPlus, Search, Phone, Shield,
  Loader2, Edit2, Trash2, RefreshCw,
} from 'lucide-react';

const BLANK = { full_name: '', email: '', phone: '', role: 'viewer', status: 'active' };

export default function OpsUsersPage() {
  const { tenant } = useTrackOticAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [assignable, setAssignable] = useState([]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await opsApi.getUsers();
      setUsers(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    opsApi.getAssignableRoles().then(r => setAssignable(r.data?.roles || [])).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(BLANK);
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      full_name: u.full_name,
      email: u.email || '',
      phone: u.phone || '',
      role: u.role || 'viewer',
      status: u.status || 'active',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.full_name?.trim() || (!form.email?.trim() && !form.phone?.trim())) {
      toast.error('Full name and a mobile (or email) are required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await opsApi.updateUser(editing.id, {
          full_name: form.full_name,
          phone: form.phone || null,
          status: form.status,
          role: form.role,
        });
        toast.success('User updated');
      } else {
        await opsApi.createUser({
          full_name: form.full_name,
          email: form.email || null,
          phone: form.phone || null,
          role: form.role,
        });
        toast.success('User added — they sign in with a one-time code on first use (no password)');
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (u) => {
    if (!window.confirm(`Deactivate "${u.full_name}"? They will lose access.`)) return;
    try {
      await opsApi.deactivateUser(u.id);
      toast.success('User deactivated');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to deactivate');
    }
  };

  const filtered = users.filter(
    (u) =>
      u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const activeCount = users.filter((u) => u.status === 'active').length;
  const inactiveCount = users.filter((u) => u.status !== 'active').length;

  return (
    <div className="p-8" data-testid="ops-users-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <Users className="w-8 h-8 text-teal-500" />
            User Management
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">Manage users for {tenant?.name}</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={fetchUsers} variant="outline" className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300" data-testid="ops-users-refresh">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={openCreate} className="bg-teal-600 hover:bg-teal-700" data-testid="ops-add-user-btn">
            <UserPlus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <StatCard icon={Users} label="Total" value={users.length} color="blue" />
        <StatCard icon={Shield} label="Active" value={activeCount} color="green" valueClass="text-green-700 dark:text-green-400" />
        <StatCard icon={Shield} label="Inactive" value={inactiveCount} color="slate" valueClass="text-slate-600 dark:text-slate-400" />
      </div>

      {/* Search */}
      <div className="relative max-w-md mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
        <Input
          placeholder="Search users..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-800 text-slate-900 dark:text-white"
          data-testid="ops-users-search"
        />
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-200 dark:border-slate-800">
              <TableHead className="text-slate-500 dark:text-slate-400">User</TableHead>
              <TableHead className="text-slate-500 dark:text-slate-400">Contact</TableHead>
              <TableHead className="text-slate-500 dark:text-slate-400">Status</TableHead>
              <TableHead className="text-slate-500 dark:text-slate-400">Created</TableHead>
              <TableHead className="text-slate-500 dark:text-slate-400 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-teal-500 mx-auto" />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-slate-500">
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((u) => (
                <TableRow key={u.id} className="border-slate-200 dark:border-slate-800" data-testid={`ops-user-row-${u.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-teal-600 flex items-center justify-center text-white font-medium">
                        {(u.full_name || '?').split(' ').map((n) => n[0]).join('').slice(0, 2)}
                      </div>
                      <div>
                        <p className="text-slate-900 dark:text-white font-medium">{u.full_name}</p>
                        <p className="text-slate-500 text-sm">{u.email?.endsWith('@invite.local') ? (u.phone || '—') : u.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                      <Phone className="w-4 h-4 text-slate-500" />
                      {u.phone || '—'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={u.status === 'active' ? 'bg-green-500/20 text-green-700 dark:text-green-400 border-green-500/30' : 'bg-slate-500/20 text-slate-600 dark:text-slate-400 border-slate-500/30'}>
                      {u.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-slate-600 dark:text-slate-400 text-sm">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(u)}
                        className="h-8 w-8 text-slate-500 dark:text-slate-400 hover:text-teal-600 dark:hover:text-teal-400"
                        data-testid={`edit-ops-user-${u.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeactivate(u)}
                        className="h-8 w-8 text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400"
                        disabled={u.status !== 'active'}
                        data-testid={`delete-ops-user-${u.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white">
              {editing ? 'Edit User' : 'Add User'}
            </DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400">
              {editing ? 'Update user info or change status.' : 'Add a new user to your customer organization.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-3">
            <div>
              <Label className="text-slate-700 dark:text-slate-300">Full Name *</Label>
              <Input
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                placeholder="Jane Doe"
                className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white mt-1"
                data-testid="ops-user-name"
              />
            </div>
            <div>
              <Label className="text-slate-700 dark:text-slate-300">Email (or mobile below)</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="jane@company.com"
                className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white mt-1"
                disabled={!!editing}
                data-testid="ops-user-email"
              />
            </div>
            <div>
              <Label className="text-slate-700 dark:text-slate-300">Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+91 9876543210"
                className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white mt-1"
                data-testid="ops-user-phone"
              />
            </div>
            <div>
              <Label className="text-slate-700 dark:text-slate-300">Role (at or below yours)</Label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full mt-1 rounded-md border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white px-3 py-2 text-sm"
                data-testid="ops-user-role"
              >
                {assignable.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
              </select>
              <p className="text-xs text-slate-500 mt-1">No password: the user signs in with a one-time code sent to their mobile or email.</p>
            </div>
            {editing && (
              <div>
                <Label className="text-slate-700 dark:text-slate-300">Status</Label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full mt-1 rounded-md border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white px-3 py-2 text-sm"
                  data-testid="ops-user-status"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="ghost" onClick={() => setDialogOpen(false)} className="flex-1 text-slate-700 dark:text-slate-300">
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-teal-600 hover:bg-teal-700"
                data-testid="ops-save-user-btn"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editing ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, valueClass = 'text-slate-900 dark:text-white' }) {
  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6">
      <div className="flex items-center gap-3 mb-2">
        <Icon className={`w-5 h-5 text-${color}-400`} />
        <span className="text-slate-600 dark:text-slate-400 text-sm">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${valueClass}`}>{value}</p>
    </div>
  );
}
